import { useState } from 'react'
import {
  Check,
  Send,
  Search,
  ChevronRight,
  MoreHorizontal,
  Users,
  User,
  Briefcase,
  Megaphone,
  Brain,
  Globe,
  Lock,
  BookOpen,
  Code2,
  Bot,
  Hammer,
  Music,
} from 'lucide-react'
import BrandLogo from '../components/BrandLogo'
import OrangeOrb3D from '../components/OrangeOrb3D'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Product Deck
   Two-column layout: copy left, glassmorphism mock UI right. Same branding.
   ═══════════════════════════════════════════════════════════════════════════ */

const DECK_PASSWORD = 'dizee2026'

export default function ProductDeckPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === DECK_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  if (!authenticated) {
    return (
      <div
        className="deck-final-login fixed inset-0 flex items-center justify-center p-4 sm:p-8"
        style={{ backgroundColor: '#13120A' }}
      >
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0" />
              <BrandLogo className="h-3 w-auto text-white" />
            </div>
            <p className="text-sm text-zinc-500">Enter the password to view this deck.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Password"
                autoFocus
                autoComplete="current-password"
                className="deck-final-input w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-base text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/50 focus:border-transparent"
              />
              {error && (
                <p className="mt-2 text-xs text-red-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="deck-final-input w-full rounded-lg bg-white px-4 py-3 text-base font-semibold text-zinc-950 hover:bg-zinc-100 transition-colors"
            >
              View Deck
            </button>
          </form>
          <p className="mt-8 text-[11px] text-zinc-700">
            Confidential — For intended recipients only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#13120A' }}>
      <div className="max-w-[1400px] mx-auto px-8 sm:px-16 lg:px-24">
        {SLIDES.map(({ component: Section }, i) => (
          <section
            key={i}
            className={`py-24 sm:py-32 ${i === 0 ? 'min-h-screen flex flex-col justify-center' : ''} ${i < SLIDES.length - 1 ? 'border-b border-white/[0.04]' : ''}`}
          >
            <Section />
          </section>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   LAYOUT & MOCK COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

interface Slide {
  title: string
  component: React.FC
}

function SlideTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest">
      {children}
    </span>
  )
}

/** Two-column slide: left = copy, right = mock UI card(s) */
function SlideLayout({
  tag,
  headline,
  highlight,
  highlightClassName,
  subline,
  bullets,
  roles,
  bottomLine,
  children,
}: {
  tag?: string
  headline: string
  highlight?: string
  highlightClassName?: string
  subline?: string
  bullets?: string[]
  roles?: string[]
  bottomLine?: string
  children: React.ReactNode
}) {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        {tag && <SlideTag>{tag}</SlideTag>}
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08]">
          {headline}
          {highlight && (
            <span className={`text-[#FF2300] ${highlightClassName ?? ''}`}>{highlight}</span>
          )}
        </h2>
        {subline && <p className="mt-4 text-base sm:text-lg text-zinc-400">{subline}</p>}
        {bullets && (
          <ul className="mt-6 space-y-3">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
                <span className="text-zinc-300">{b}</span>
              </li>
            ))}
          </ul>
        )}
        {roles && (
          <div className="mt-8 flex flex-wrap items-center gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="w-9 h-9 rounded-full bg-white/10 border border-white/10" />
            ))}
            <span className="text-zinc-500 mx-1">···</span>
            <div className="flex flex-wrap gap-2">
              {roles.map((r) => (
                <span key={r} className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[13px] text-zinc-300">
                  {r}
                </span>
              ))}
            </div>
          </div>
        )}
        {bottomLine && (
          <p className="mt-6 text-sm text-zinc-500 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500/80" />
            {bottomLine}
          </p>
        )}
      </div>
      <div className="relative flex items-center justify-center min-h-[320px] lg:min-h-[400px]">
        {children}
      </div>
    </div>
  )
}

/** Glassmorphism mock card with optional glow */
function MockCard({
  className = '',
  glow,
  children,
}: {
  className?: string
  glow?: boolean
  children: React.ReactNode
}) {
  return (
    <div
      className={`rounded-2xl border border-white/10 bg-white/[0.06] backdrop-blur-xl ${glow ? 'shadow-[0_0_60px_-12px_rgba(255,35,0,0.2)]' : ''} ${className}`}
      style={{ boxShadow: glow ? '0 0 60px -12px rgba(255,35,0,0.2)' : undefined }}
    >
      {children}
    </div>
  )
}

/* ── Mock: Update Ticket Counts ─────────────────────────────────────────── */
function MockTicketCounts() {
  const rows = [
    { name: 'Tempo', loc: 'Philadelphia · Mar 20, 2026', cap: '400', count: '328', pct: 82, status: 'On Sale', saved: true },
    { name: 'Stage West', loc: 'State College · Mar 21, 2026', cap: '1,200', count: '915', pct: 76, status: 'Venue TBA', saved: true },
    { name: 'Pal Norte', loc: 'Monterrey · Mar 27, 2026', cap: 'TBA', count: '–', pct: 0, status: 'Capacity TBA', saved: false },
  ]
  return (
    <MockCard className="w-full max-w-lg p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-white">Update Ticket Counts</h3>
          <p className="text-[13px] text-zinc-500">19 Upcoming Shows</p>
        </div>
        <div className="flex gap-2">
          <button className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400">Import</button>
          <button className="rounded-lg bg-[#FF2300] px-3 py-1.5 text-[12px] font-medium text-white">Save All</button>
        </div>
      </div>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-white">{r.name}</span>
              <div className="flex items-center gap-2">
                {r.saved && <span className="text-[11px] text-emerald-500">✔ Saved</span>}
                {!r.saved && <span className="text-[11px] text-zinc-500">• Pending</span>}
                <span className="text-[10px] text-zinc-500 border border-white/10 rounded px-1.5">{r.status}</span>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mb-2">{r.loc}</p>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div><span className="text-zinc-500">Sellable Cap</span><div className="font-medium text-white">{r.cap}</div></div>
              <div><span className="text-zinc-500">Ticket Count</span><div className="font-medium text-white">{r.count}</div></div>
              <div><span className="text-zinc-500">% Sold</span><div className="font-medium text-[#FF2300]">{r.pct ? `${r.pct}%` : '–'}</div></div>
            </div>
            {r.pct > 0 && (
              <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full bg-[#FF2300]" style={{ width: `${r.pct}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>
    </MockCard>
  )
}

/* ── Mock: Team tab ─────────────────────────────────────────────────────── */
function MockTeamTab() {
  const members = [
    { name: 'Charly', email: 'charly@spacecamp.la', role: 'Owner' },
    { name: 'charlymanager', email: 'charlyteam@spacecamp.la', role: 'Manager' },
    { name: 'promoter', email: 'promoter@spacecamp.la', role: 'Manager' },
  ]
  return (
    <MockCard className="w-full max-w-md p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-4 border-b border-white/10">
          <span className="text-[13px] text-zinc-500 pb-2">Schedule</span>
          <span className="text-[13px] text-zinc-500 pb-2">Shows</span>
          <span className="text-[13px] font-medium text-white border-b-2 border-[#FF2300] pb-2">Team</span>
        </div>
        <button className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400">Invite Team Member</button>
      </div>
      <div className="space-y-0 divide-y divide-white/5">
        {members.map((m, i) => (
          <div key={i} className="flex items-center gap-3 py-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-medium text-white">{m.name}</div>
              <div className="text-[12px] text-zinc-500">{m.email}</div>
            </div>
            <div className="flex items-center gap-1.5 text-[12px] text-zinc-500">
              <span className="w-2 h-2 rounded-full bg-emerald-500/80" />
              {m.role}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
        <span className="text-[13px] text-zinc-500 flex-1">How&apos;d the show in Indy sell?</span>
        <button className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><Send className="w-4 h-4 text-[#FF2300]" /></button>
      </div>
    </MockCard>
  )
}

/* ── Mock: Manage Tasks ────────────────────────────────────────────────── */
function MockManageTasks() {
  const tasks = [
    { tag: 'BRAND DEAL', title: 'Draft IG Story for Genius promotion', due: 'Due Tomorrow', status: 'In progress' },
    { tag: 'DSP PITCH', title: 'Pitch single to Spotify editors', due: 'Due Friday', status: 'Tomorrow' },
    { tag: 'MARKETING', title: 'Approve artwork v3', due: 'Apr 9, 2026', status: 'Not started' },
  ]
  return (
    <MockCard className="w-full max-w-lg p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-white">Manage Tasks</h3>
          <p className="text-[13px] text-zinc-500">6 Total Tasks</p>
        </div>
        <button className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400 flex items-center gap-1">Add Tasks ▾</button>
      </div>
      <div className="flex gap-2 mb-3 border-b border-white/10">
        {['ALL', 'EDIT', 'DSP', 'BRAND'].map((t, i) => (
          <span key={t} className={`text-[12px] pb-2 ${i === 3 ? 'text-white font-medium border-b-2 border-[#FF2300]' : 'text-zinc-500'}`}>{t}</span>
        ))}
      </div>
      <div className="rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 flex items-center gap-2 mb-3">
        <Search className="w-3.5 h-3.5 text-zinc-500" />
        <span className="text-[12px] text-zinc-500">Filter by keyword...</span>
      </div>
      <div className="space-y-2">
        {tasks.map((t, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] p-3 flex items-center gap-3">
            <span className="text-[10px] font-medium bg-[#FF2300]/20 text-[#FF2300] px-2 py-0.5 rounded">{t.tag}</span>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-white">{t.title}</div>
              <div className="text-[11px] text-zinc-500">{t.due}</div>
            </div>
            <div className="w-8 h-8 rounded-full bg-white/10 flex-shrink-0" />
            <span className="text-[11px] text-zinc-500 flex items-center gap-1">
              {t.status === 'In progress' && <span className="w-1.5 h-1.5 rounded-full bg-[#FF2300]" />}
              {t.status}
            </span>
            <MoreHorizontal className="w-4 h-4 text-zinc-500 flex-shrink-0" />
          </div>
        ))}
      </div>
    </MockCard>
  )
}

/* ── Mock: Ask Dizee / Key Insights ─────────────────────────────────────── */
function MockAskDizee() {
  return (
    <MockCard className="w-full max-w-md p-5" glow>
      <h3 className="text-base font-semibold text-white mb-3">Ask Dizee.</h3>
      <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 flex items-center gap-2 mb-4">
        <Search className="w-4 h-4 text-zinc-500" />
        <span className="text-[13px] text-zinc-500 flex-1">Ask anything...</span>
        <Send className="w-4 h-4 text-[#FF2300]" />
      </div>
      <div className="space-y-3">
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 rounded-full bg-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3 h-3 text-emerald-400" /></div>
          <div><div className="text-[13px] text-white">Shrine - 7,842 / 10,500 (75% sold)</div></div>
        </div>
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 rounded-full bg-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3 h-3 text-emerald-400" /></div>
          <div>
            <div className="text-[13px] text-white">Today&apos;s top three opening Spotify playlists</div>
            <div className="flex gap-1 mt-1">
              <span className="w-2 h-2 rounded-sm bg-[#FF2300]" /><span className="text-[11px] text-zinc-500">Mellow Mood</span>
              <span className="w-2 h-2 rounded-sm bg-amber-600" /><span className="text-[11px] text-zinc-500">Lo-Fi Beats</span>
              <span className="w-2 h-2 rounded-sm bg-zinc-600" /><span className="text-[11px] text-zinc-500">Young & Free</span>
            </div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 rounded-full bg-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-[10px] font-semibold text-emerald-400">$</span></div>
          <div><div className="text-[13px] text-white font-semibold">$1,235.89 estimated weekly</div><div className="text-[11px] text-zinc-500">Royalties Sunday</div></div>
        </div>
        <div className="flex items-start gap-2">
          <div className="w-5 h-5 rounded-full bg-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-[10px] font-semibold text-emerald-400">S</span></div>
          <div><div className="text-[13px] text-white">Nashville: Thursday flights, first show Friday</div><div className="text-[11px] text-zinc-500">Due Apr 13, 2026</div></div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-white/10 rounded-lg border border-white/10 bg-white/5 px-3 py-2 flex items-center gap-2">
        <span className="text-[13px] text-zinc-500 flex-1">How&apos;d the show in Indy sell?</span>
        <Send className="w-4 h-4 text-[#FF2300]" />
      </div>
    </MockCard>
  )
}

/* ── Mock: Dashboard overview ────────────────────────────────────────────── */
function MockDashboard() {
  return (
    <MockCard className="w-full max-w-lg p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-white">Overview</h3>
        <span className="text-[11px] text-emerald-500">Live</span>
      </div>
      <div className="flex gap-3 border-b border-white/10 pb-3 mb-4 overflow-x-auto">
        {['Overview', 'Shows', 'Music', 'Tasks'].map((t, i) => (
          <span key={t} className={`text-[12px] whitespace-nowrap ${i === 0 ? 'text-white font-medium' : 'text-zinc-500'}`}>{t}</span>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
          <div className="text-2xl font-semibold text-white">42</div>
          <div className="text-[12px] text-zinc-500">Shows</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
          <div className="text-2xl font-semibold text-white">53</div>
          <div className="text-[12px] text-zinc-500">Tasks</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 col-span-2">
          <div className="text-[13px] text-zinc-400">This week · 6 releases, 4 shows, 12 open tasks</div>
          <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full w-[68%] rounded-full bg-[#FF2300]/80" />
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">Career pulse at a glance</p>
        </div>
      </div>
    </MockCard>
  )
}

/* ── Mock: Music catalog ─────────────────────────────────────────────────── */
function MockMusicCatalog() {
  return (
    <MockCard className="w-full max-w-lg p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-white">Music</h3>
        <button className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400">Add release</button>
      </div>
      <div className="text-[11px] text-zinc-500 uppercase tracking-wider mb-3">Catalog · Releases · Streams</div>
      <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 flex items-center gap-3">
        <img src="/deck/onmymind.png" alt="On My Mind" className="w-14 h-14 rounded-lg object-cover shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-white truncate">On My Mind</p>
          <p className="text-[12px] text-zinc-500">Diplo, Sidepiece · 334M streams</p>
          <p className="text-[11px] text-zinc-600 mt-1">Metadata · Splits · Credits</p>
        </div>
      </div>
      <div className="mt-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[12px] text-zinc-500">+ 12 more releases in catalog</div>
    </MockCard>
  )
}

/* ── Mock: AI insights ───────────────────────────────────────────────────── */
function MockAIIntelligence() {
  return (
    <MockCard className="w-full max-w-lg p-5" glow>
      <div className="flex items-center gap-2 mb-4">
        <Brain className="w-5 h-5 text-[#FF2300]" />
        <h3 className="text-base font-semibold text-white">Insights</h3>
        <span className="text-[10px] text-zinc-500 uppercase tracking-wider ml-auto">In context</span>
      </div>
      <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
        <div className="flex items-start gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0 mt-1.5" />
          <div>
            <p className="text-[13px] text-zinc-200">LA is pacing ~20% slower than your last LA market run.</p>
            <p className="text-[11px] text-zinc-500 mt-1">990 sold · venue cap 1,200</p>
          </div>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full w-[83%] rounded-full bg-[#FF2300]/90" />
        </div>
        <p className="text-[12px] text-[#FF2300] font-medium">$2K bonus unlocks at 1,000 sold — 10 tickets to go.</p>
      </div>
      <div className="mt-4 rounded-lg border border-white/10 bg-white/5 px-3 py-2 flex items-center gap-2">
        <Search className="w-4 h-4 text-zinc-500" />
        <span className="text-[13px] text-zinc-500 flex-1">Ask about pacing, guarantees, or playlists…</span>
        <Send className="w-4 h-4 text-[#FF2300]" />
      </div>
    </MockCard>
  )
}

/* ── Mock: 24/7 sync status ─────────────────────────────────────────────── */
function Mock247Sync() {
  const rows = [
    { label: 'Google Sheets', status: 'Live', detail: 'Show rows & ticket %' },
    { label: 'Google Calendar', status: 'Synced', detail: 'Schedule & holds' },
    { label: 'Email & alerts', status: 'Active', detail: 'Team notifications' },
    { label: 'Last full sync', status: '2m ago', detail: 'Automatic' },
  ]
  return (
    <MockCard className="w-full max-w-lg p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-white">Sync status</h3>
        <span className="flex items-center gap-1.5 text-[11px] text-emerald-400">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          Always on
        </span>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.label} className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 flex items-center justify-between gap-3">
            <div>
              <p className="text-[13px] text-white font-medium">{r.label}</p>
              <p className="text-[11px] text-zinc-500">{r.detail}</p>
            </div>
            <span className="text-[11px] font-medium text-emerald-400 shrink-0">{r.status}</span>
          </div>
        ))}
      </div>
      <p className="mt-4 text-[12px] text-zinc-500">Changes flow in both directions — no duplicate entry, no stale tabs.</p>
    </MockCard>
  )
}

/* ── Mock: Schedule / Calendar ───────────────────────────────────────────── */
function MockSchedule() {
  return (
    <MockCard className="w-full max-w-md p-5" glow>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-white">Schedule</h3>
        <button className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] text-zinc-400">Connect Google <ChevronRight className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex gap-4 mb-4">
        <div className="text-3xl font-semibold text-white">31</div>
        <div className="text-3xl font-semibold text-white">19</div>
        <div className="text-3xl font-semibold text-zinc-500">12</div>
      </div>
      <div className="space-y-2 text-[13px]">
        <div className="text-zinc-400">LA to NY flights (Charly to approve)</div>
        <div className="text-zinc-400">Call Haley Monday</div>
        <div className="text-zinc-400">Task Reley Monday</div>
      </div>
    </MockCard>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SHARED HELPERS (ported from investor deck)
   ═══════════════════════════════════════════════════════════════════════════ */

function DizeeWord({ className = '' }: { className?: string }) {
  return <span className={className || undefined}>DIZEE</span>
}

const PROBLEM_LOGOS: { label: string; src: string }[] = [
  { label: 'iMessage', src: '/logos/deck/apple.svg' },
  { label: 'Asana', src: '/logos/deck/asana.svg' },
  { label: 'ASCAP', src: '/logos/deck/ascap.svg' },
  { label: 'Gmail', src: '/logos/deck/gmail.svg' },
  { label: 'Google Sheets', src: '/logos/deck/googlesheets.svg' },
  { label: 'MasterTour', src: '/logos/deck/mastertour.svg' },
  { label: 'Slack', src: '/logos/deck/slack.svg' },
  { label: 'Spotify', src: '/logos/deck/spotify.svg' },
]

const SOLUTION_ITEMS: (
  | { type: 'logo'; label: string; src: string }
  | { type: 'role'; label: string; Icon: React.ComponentType<{ className?: string }> }
)[] = [
  ...PROBLEM_LOGOS.map((l) => ({ type: 'logo' as const, label: l.label, src: l.src })),
  { type: 'role', label: 'Artist', Icon: User },
  { type: 'role', label: 'Agent', Icon: Briefcase },
  { type: 'role', label: 'Promoter', Icon: Megaphone },
  { type: 'role', label: 'Managers', Icon: Users },
]

/** Hub diagram: tools and roles wired into Dizee (legacy “The Solution” visual). */
function MockCentralDizzy() {
  const radius = 38
  const positions = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => {
    const rad = (deg * Math.PI) / 180
    const x = 50 + radius * Math.sin(rad)
    const y = 50 - radius * Math.cos(rad)
    return { x, y, left: `${x.toFixed(1)}%`, top: `${y.toFixed(1)}%` }
  })

  return (
    <div className="relative w-full max-w-lg flex flex-col items-center">
      <div className="relative w-full aspect-square max-h-[280px] sm:max-h-[320px] md:max-h-[380px] mx-auto">
        {SOLUTION_ITEMS.map((item, i) => (
          <div
            key={i}
            className="absolute flex flex-col items-center justify-center gap-1.5 z-10 -translate-x-1/2 -translate-y-1/2"
            style={{ left: positions[i].left, top: positions[i].top }}
          >
            {item.type === 'logo' ? (
              <img src={item.src} alt={item.label} className="h-7 w-auto object-contain sm:h-10" />
            ) : (
              <div className="h-7 w-7 sm:h-10 sm:w-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-zinc-400 shrink-0">
                <item.Icon className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
              </div>
            )}
            <span className="text-[9px] sm:text-[11px] text-zinc-500 whitespace-nowrap">{item.label}</span>
          </div>
        ))}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
          {positions.map((pos, i) => (
            <line
              key={i}
              x1={pos.x}
              y1={pos.y}
              x2={50}
              y2={50}
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="0.35"
              strokeDasharray="1.5 1"
            />
          ))}
        </svg>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center gap-1.5 z-10">
          <div className="w-7 h-7 bg-[#FF2300] rounded-sm flex-shrink-0" />
          <BrandLogo className="h-5 w-auto text-white" />
        </div>
      </div>
    </div>
  )
}

/* Google Sheets–style preview (representative synced grid) */
function MockSheetsSyncPreview() {
  const headers = ['Show', 'Date', 'Venue', 'Guarantee', 'Sold %', 'Status']
  const rows = [
    ['Brooklyn Storehouse', 'Mar 14', 'NY', '$10,000', '48.7%', 'On sale'],
    ['The Midway', 'Mar 22', 'SF', '$8,500', '82%', 'On sale'],
    ['Radius', 'Apr 02', 'CHI', '$12,000', '61%', 'On sale'],
  ]
  return (
    <div className="w-full rounded-lg border border-white/[0.12] overflow-hidden bg-[#0d1117] text-left shadow-lg">
      <div className="flex items-center gap-2 px-2 py-1.5 border-b border-white/10 bg-white/[0.04]">
        <img src="/logos/deck/googlesheets.svg" alt="" className="h-4 w-4 opacity-90" />
        <span className="text-[10px] text-zinc-400 font-medium">Shows · synced with Sheets</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px] sm:text-[11px]">
          <thead>
            <tr className="bg-[#0f5132]/90 text-white/95">
              {headers.map((h) => (
                <th key={h} className="text-left font-semibold px-2 py-1.5 border-r border-white/10 last:border-r-0 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-zinc-300">
            {rows.map((row, ri) => (
              <tr key={ri} className="border-t border-white/[0.06] bg-white/[0.02]">
                {row.map((cell, ci) => (
                  <td key={ci} className="px-2 py-1.5 border-r border-white/[0.04] last:border-r-0 whitespace-nowrap">
                    {ci === 4 ? <span className="text-[#FF2300] font-medium">{cell}</span> : cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Three data layers + structure → harness; anything music plugs in and inherits full power */
function MockHarnessDiagram() {
  const inputs = [
    { label: 'Public data', sub: 'Market signal', Icon: Globe },
    { label: 'Your private data', sub: 'Shows, releases, teams', Icon: Lock },
    { label: 'Industry intelligence', sub: 'How music actually runs', Icon: BookOpen },
  ]
  return (
    <MockCard className="w-full max-w-lg p-6" glow>
      <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest text-center mb-5">
        The three fuels · one structure
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {inputs.map(({ label, sub, Icon }) => (
          <div
            key={label}
            className="rounded-xl border border-white/[0.08] bg-white/[0.03] px-3 py-3 text-center"
          >
            <Icon className="w-4 h-4 text-[#FF2300] mx-auto mb-2" />
            <p className="text-[11px] font-semibold text-white leading-tight">{label}</p>
            <p className="text-[10px] text-zinc-500 mt-1 leading-snug">{sub}</p>
          </div>
        ))}
      </div>
      <div className="flex justify-center my-3">
        <div className="h-8 w-px bg-gradient-to-b from-white/25 to-[#FF2300]/50" aria-hidden />
      </div>
      <div className="flex flex-col items-center rounded-xl border border-[#FF2300]/25 bg-[#FF2300]/[0.06] px-4 py-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-[#FF2300] rounded-sm flex-shrink-0" />
          <BrandLogo className="h-5 w-auto text-white" />
        </div>
        <p className="text-[11px] text-zinc-400 mt-2 text-center leading-snug">
          <DizeeWord className="text-white font-medium" /> harness · one source of truth · built for music
        </p>
      </div>
    </MockCard>
  )
}

/** 3D orb → API bar → curved dashed spokes + icon chips (same language as Why Dizee hub). */
function MockOSAPIDiagram() {
  const items: {
    label: string
    Icon: React.ComponentType<{ className?: string }>
    labelClassName?: string
  }[] = [
    { label: 'Developers', Icon: Code2 },
    { label: 'Agents', Icon: Bot },
    { label: 'New builds', Icon: Hammer },
    { label: 'AI tools', Icon: Brain },
    { label: 'Any music tech', Icon: Music, labelClassName: 'whitespace-nowrap' },
  ]
  const w = 360
  const hubX = w / 2
  const hubY = 8
  const tipY = 84
  const pad = 26
  const xs = items.map((_, i) => pad + (i * (w - 2 * pad)) / (items.length - 1))

  return (
    <MockCard className="w-full max-w-lg p-6 sm:p-8" glow>
      <div className="flex flex-col items-center text-center">
        <div className="relative z-10 mx-auto h-[160px] w-[160px]">
          <OrangeOrb3D size={160} />
        </div>

        <div className="relative z-10 flex w-full max-w-xs items-center gap-3 my-5">
          <div className="h-0 flex-1 border-t border-dashed border-[#FF2300]/70" />
          <span className="text-[10px] font-semibold uppercase tracking-widest text-[#FF2300] shrink-0">API</span>
          <div className="h-0 flex-1 border-t border-dashed border-[#FF2300]/70" />
        </div>

        <p className="relative z-10 mb-4 max-w-sm text-[11px] text-zinc-400 leading-snug px-1">
          Apps, agents, and new products — all tapping the same Dizee layer
        </p>

        <div className="relative w-full max-w-[360px] mx-auto">
          <svg
            className="block w-full max-w-[360px] mx-auto overflow-visible"
            style={{ height: 96 }}
            viewBox={`0 0 ${w} 96`}
            preserveAspectRatio="xMidYMin meet"
            aria-hidden
          >
            <defs>
              <marker
                id="deckLayerApiArrow"
                markerWidth="5"
                markerHeight="5"
                refX="4.2"
                refY="2.5"
                orient="auto"
              >
                <path d="M0,0 L5,2.5 L0,5 Z" fill="rgba(255,35,0,0.55)" />
              </marker>
            </defs>
            {xs.map((x2, i) => {
              const dx = x2 - hubX
              const c1x = hubX + dx * 0.38
              const c1y = hubY + 26
              const c2x = hubX + dx * 0.78
              const c2y = tipY - 20
              const d = `M ${hubX} ${hubY} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${x2} ${tipY}`
              return (
                <path
                  key={i}
                  d={d}
                  fill="none"
                  stroke="rgba(255,35,0,0.42)"
                  strokeWidth="1.2"
                  strokeDasharray="3 2.5"
                  markerEnd="url(#deckLayerApiArrow)"
                />
              )
            })}
          </svg>

          <div className="grid grid-cols-5 gap-x-2.5 sm:gap-x-4 gap-y-1.5 w-full -mt-1">
            {items.map(({ label, Icon, labelClassName }) => (
              <div key={label} className="flex flex-col items-center gap-2 text-center min-w-0 px-0">
                <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-zinc-400 shrink-0">
                  <Icon className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
                </div>
                <span
                  className={`text-[9px] sm:text-[11px] text-zinc-300 leading-tight ${labelClassName ?? ''}`}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-6 text-[11px] text-zinc-500 leading-relaxed max-w-sm">
          Plug in here and you get the full picture Dizee already holds — instead of rebuilding how music works on your own.
        </p>
      </div>
    </MockCard>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDES
   ═══════════════════════════════════════════════════════════════════════════ */

/** Opening aligned with /deck-final and /cool-deck-final (SlideTitle). */
function SlideTitle() {
  return (
    <div>
      <div className="flex items-center gap-5 mb-8">
        <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-[#FF2300] rounded-sm flex-shrink-0" aria-hidden />
        <BrandLogo className="h-10 sm:h-12 lg:h-14 w-auto text-white" />
      </div>
      <p className="mt-6 text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-white leading-snug max-w-3xl">
        The <span className="text-[#FF2300]">new operating system</span> and{' '}
        <span className="text-[#FF2300]">source of truth</span>
        {' '}for modern music careers.
      </p>
      <div className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-2 text-base sm:text-[17px] text-white">
        <span className="inline-flex items-center rounded-full border border-[#FF2300]/30 bg-[#FF2300]/10 px-4 py-2 text-[14px] sm:text-[15px] font-medium text-[#FF2300]">
          Product
        </span>
        <span>Spring 2026</span>
        <span>dizee.com</span>
      </div>
    </div>
  )
}

/** Harness: anything in music that plugs into Dizee inherits ultimate power from the three layers + structure. */
function SlideIndustryHarness() {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div className="min-w-0">
        <SlideTag>The industry harness</SlideTag>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08]">
          A harness for{' '}
          <span className="text-[#FF2300]">everything</span>{' '}
          built or run in music.
        </h2>
        <p className="mt-5 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-xl">
          <DizeeWord className="text-white font-medium" /> isn’t just an app you log into — it’s the layer that every music career can plug into. When it connects, it automatically becomes{' '}
          <span className="text-white font-medium">far more powerful</span>
          {' '}because it inherits public reality, your private world, decades of industry shape, and a system built in the right structure.
        </p>
        <ul className="mt-6 space-y-3 max-w-xl">
          <li className="flex gap-3 items-start">
            <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
            <span className="text-zinc-300">
              <span className="text-white font-medium">Public data</span> — market signal, benchmarks, and context the whole industry shares.
            </span>
          </li>
          <li className="flex gap-3 items-start">
            <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
            <span className="text-zinc-300">
              <span className="text-white font-medium">Your private data</span> — your shows, releases, deals, and team — permissioned and kept in one place.
            </span>
          </li>
          <li className="flex gap-3 items-start">
            <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
            <span className="text-zinc-300">
              <span className="text-white font-medium">Industry experience</span> — how touring, rights, and money actually work, encoded so tools behave like insiders.
            </span>
          </li>
        </ul>
        <p className="mt-8 text-sm text-zinc-500 flex items-center gap-2 max-w-xl">
          <span className="w-2 h-2 rounded-full bg-emerald-500/80 shrink-0" />
          Plug in → inherit the full stack. That’s the harness.
        </p>
      </div>
      <div className="relative flex items-center justify-center min-h-[320px] lg:min-h-[400px]">
        <MockHarnessDiagram />
      </div>
    </div>
  )
}

/** The layer & API: industry + tools share one foundation. */
function SlideOperatingSystemAPI() {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div className="min-w-0">
        <SlideTag>The layer &amp; API</SlideTag>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08]">
          We&apos;ve built the{' '}
          <span className="text-[#FF2300]">layer</span>{' '}
          that the music industry and other tools are built on.
        </h2>
        <p className="mt-5 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-xl">
          Think of <DizeeWord className="text-white font-medium" /> as the floor, not just a room: teams run on the OS every day, and{' '}
          <span className="text-white font-medium">everything else in music</span> — new apps, agents, AI, custom builds — can stand on the same foundation instead of reinventing it.
        </p>
        <ul className="mt-6 space-y-3 max-w-xl">
          <li className="flex gap-3 items-start">
            <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
            <span className="text-zinc-300">
              One shared picture of shows, releases, music data, money, and teams — so what you build lines up with what everyone else sees.
            </span>
          </li>
          <li className="flex gap-3 items-start">
            <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
            <span className="text-zinc-300">
              The <span className="text-white font-medium">API</span> is that same foundation, opened up — not a separate, weaker version for outsiders.
            </span>
          </li>
          <li className="flex gap-3 items-start">
            <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
            <span className="text-zinc-300">
              <span className="text-white font-medium">Any AI or agent</span> can plug into Dizee — so new tools strengthen the music industry instead of guessing in isolation.
            </span>
          </li>
        </ul>
        <p className="mt-8 text-sm text-zinc-500 flex items-center gap-2 max-w-xl">
          <span className="w-2 h-2 rounded-full bg-emerald-500/80 shrink-0" />
          The industry builds upward from here — we built what’s underneath.
        </p>
      </div>
      <div className="relative flex items-center justify-center min-h-[320px] lg:min-h-[400px]">
        <MockOSAPIDiagram />
      </div>
    </div>
  )
}

function SlideDashboard() {
  return (
    <SlideLayout
      tag="Dashboard"
      headline="Your whole career, one "
      highlight="live overview."
      highlightClassName="whitespace-nowrap"
      subline="Shows, music, tasks, and what needs attention — surfaced together so nobody is guessing."
      bullets={[
        'At-a-glance counts and status across touring, releases, and workstreams.',
        'Jump into any area without losing context of the full picture.',
      ]}
      bottomLine="Start every day knowing where things stand."
    >
      <MockDashboard />
    </SlideLayout>
  )
}

function SlideMusic() {
  return (
    <SlideLayout
      tag="Music"
      headline="Catalog, releases, and credits in "
      highlight="one place."
      highlightClassName="whitespace-nowrap"
      subline="Discography, metadata, splits, and stream performance — structured for managers and artists alike."
      bullets={[
        'Track releases and catalog health alongside the rest of the career.',
        'Credits and splits stay attached to the work, not buried in folders.',
      ]}
      bottomLine="The music side of the business, as organized as the tour side."
    >
      <MockMusicCatalog />
    </SlideLayout>
  )
}

function SlideShows() {
  return (
    <SlideLayout
      tag="Shows"
      headline="Touring and ticket data you can "
      highlight="trust."
      highlightClassName="whitespace-nowrap"
      subline="Capacities, counts, sell-through, and status — updated and visible to everyone who needs them."
      bullets={[
        'See every upcoming date with guarantee and ticket context.',
        'Spot what’s on track, what’s at risk, and what still needs a save.',
      ]}
      bottomLine="No more mystery numbers the night before doors."
    >
      <MockTicketCounts />
    </SlideLayout>
  )
}

function SlideAIIntelligence() {
  return (
    <SlideLayout
      tag="AI intelligence"
      headline="Answers and insights "
      highlight="in context."
      highlightClassName="whitespace-nowrap"
      subline="Factual, career-aware guidance — pacing vs past runs, guarantees, playlists, and what to do next."
      bullets={[
        'Surface risk and opportunity while numbers are still actionable.',
        'Ask in plain language; get grounded responses tied to your data.',
      ]}
      bottomLine="Less digging through threads — more deciding with clarity."
    >
      <MockAIIntelligence />
    </SlideLayout>
  )
}

function Slide247Sync() {
  return (
    <SlideLayout
      tag="24/7 sync"
      headline="Always-on sync with "
      highlight="how you work."
      highlightClassName="whitespace-nowrap"
      subline="Sheets, calendar, and notifications stay aligned with Dizee — bidirectional, automatic, no duplicate entry."
      bullets={[
        'Google Sheets and Calendar stay in lockstep with show and schedule data.',
        'Team-facing updates propagate without someone retyping the same row.',
      ]}
      bottomLine="One source of truth that stays fresh around the clock."
    >
      <Mock247Sync />
    </SlideLayout>
  )
}

function SlideSheets() {
  return (
    <SlideLayout
      tag="Google Sheets integration"
      headline="Your show data, bidirectional sync. "
      highlight="No workflow change."
      highlightClassName="whitespace-nowrap"
      subline="We meet you where you already are. Connect once, and show data and ticket counts flow between Dizee and Google Sheets automatically."
      bullets={[
        'One sheet, many tabs — ticket tab syncs across everything.',
        '~2 minutes to set up. Bidirectional sync, no duplicate entry.',
      ]}
      bottomLine="Single glance, every morning."
    >
      <div className="w-full max-w-xl mx-auto">
        <MockSheetsSyncPreview />
      </div>
    </SlideLayout>
  )
}

function SlideAccess() {
  return (
    <SlideLayout
      tag="Teams"
      headline="Everyone sees only "
      highlight="what they need."
      highlightClassName="whitespace-nowrap"
      subline="Granular access so your team has a separate view for the things they work on only. Less noise, fewer mistakes."
      bullets={[
        'Shows only — tour dates, ticket counts, promoters.',
        'Music only — releases, catalog, credits.',
        'Full access — managers and admins see the full career view.',
      ]}
      roles={['Manager', 'Booking Agent', 'Artist', 'Promoter']}
      bottomLine="Everyone sees the same thing."
    >
      <MockTeamTab />
    </SlideLayout>
  )
}

function SlideCalendar() {
  return (
    <SlideLayout
      tag="Schedule & Google Calendar"
      headline="Your schedule, integrated with "
      highlight="Google Calendar."
      highlightClassName="whitespace-nowrap"
      subline="Add items from Dizee into your Google Calendar, create a shared calendar, or pick specific calendars. See everything in one place."
      bullets={[
        'Add show releases, music releases, and tasks from Dizee to Google Calendar.',
        'Shared calendar for the whole team, or pick specific calendars.',
        'Toggle between “what’s on Google Calendar” and “everything on the artist’s career”.',
      ]}
      bottomLine="One schedule view: Dizee + Google — all in sync."
    >
      <MockSchedule />
    </SlideLayout>
  )
}

function SlideTasks() {
  return (
    <SlideLayout
      tag="Tasks"
      headline="Never miss key "
      highlight="deadlines."
      highlightClassName="whitespace-nowrap"
      subline="Need your team to handle marketing tasks? Managers track it all here."
      bullets={[
        'Create and assign tasks.',
        'Approve brand deals.',
        'Stay ahead of pitch deadlines.',
      ]}
      bottomLine="Everyone knows what to do next."
    >
      <MockManageTasks />
    </SlideLayout>
  )
}

function SlideSummary() {
  return (
    <SlideLayout
      tag="Summary"
      headline="Dizee becomes the "
      highlight="source of truth"
      highlightClassName="whitespace-nowrap"
      subline="Ask questions about: Shows, Music, Publishing, Travel, Marketing, Team. From asking your team to asking Dizee."
      bullets={[
        'Get key insights instantly — ticket sales, promo opportunities, royalties, travel.',
        'Answers for any question, in one place.',
      ]}
    >
      <MockAskDizee />
    </SlideLayout>
  )
}

function SlideMission() {
  return (
    <div className="flex flex-col justify-center">
      <SlideTag>Mission</SlideTag>
      <h2 className="mt-8 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tighter text-white leading-[1.25] max-w-4xl">
        <span className="block whitespace-normal sm:whitespace-nowrap">&quot;To unify every part of a music career within <span className="text-[#FF2300]">one intelligent system</span>,</span>
        <span className="block"><span className="whitespace-normal sm:whitespace-nowrap">enabling artists and their teams to move faster, make smarter decisions</span>,</span>
        <span className="block">and stay aligned around <span className="text-[#FF2300] whitespace-normal sm:whitespace-nowrap">a single source of truth.</span>&quot;</span>
      </h2>
    </div>
  )
}

/** Single slide: story on the left; legacy solution hub diagram on the right. */
function SlideNarrativeCombined() {
  const storyBullets: { title: string; text: React.ReactNode }[] = [
    {
      title: 'Current state',
      text: 'The stack is still manual and disconnected — spreadsheets, legacy tools, and DMs. No unified place to run a modern career.',
    },
    {
      title: 'The problem',
      text: 'Teams lack real-time visibility. Revenue and timing get decided without the full picture — opportunities expire while someone hunts for the latest numbers.',
    },
    {
      title: 'Lost context',
      text: 'The work lives in threads nobody can search. The same questions get asked again with stale answers instead of one shared, live view.',
    },
    {
      title: 'The solution',
      text: (
        <>
          <DizeeWord className="text-[#FF2300] font-semibold" /> pulls shows, tickets, schedule, releases, and team into one{' '}
          <span className="text-[#FF2300] font-semibold">operating system</span> and{' '}
          <span className="text-[#FF2300] font-semibold">source of truth</span> — many tools, one hub.
        </>
      ),
    },
  ]

  return (
    <div
      id="problems"
      className="scroll-mt-28 flex flex-col justify-center lg:min-h-[min(920px,88vh)] py-4"
    >
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 xl:gap-16 items-center">
        <div className="min-w-0">
          <SlideTag>Why Dizee</SlideTag>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[2.35rem] xl:text-5xl font-semibold tracking-tighter text-white leading-[1.08]">
            Fragmented tools can&apos;t run a{' '}
            <span className="text-[#FF2300]">modern career.</span>
          </h2>
          <ul className="mt-8 space-y-4">
            {storyBullets.map(({ title, text }) => (
              <li key={title} className="flex gap-3 items-start">
                <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
                <div>
                  <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</span>
                  <p className="mt-1 text-[15px] sm:text-base text-zinc-300 leading-snug">{text}</p>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-sm sm:text-base text-zinc-500 italic leading-snug">
            Anyone can manage a music career end-to-end when the business finally has one system.
          </p>
        </div>

        <div className="flex flex-col items-center justify-center min-w-0 min-h-[320px] lg:min-h-[400px]">
          <div className="w-full max-w-lg flex flex-col items-center">
            <MockCentralDizzy />
            <p className="mt-10 text-center text-sm text-zinc-500">Many tools → one system.</p>
          </div>
        </div>
      </div>
    </div>
  )
}

function SlidePricing() {
  const tiers = [
    {
      tier: 'Artist',
      audienceLabel: 'Independent artist',
      price: '$45',
      note: '/mo',
      annualPrice: '$459',
      annualNote: '/yr',
      annualMonthly: '$38.25',
      audience: 'Single user',
      features: ['Dashboard', 'Catalog & Release Management', 'Career Assistant', 'Schedule & Calendar', 'Tasks', '24/7 Sync'],
      plusFeatures: undefined as string[] | undefined,
      popular: false,
    },
    {
      tier: 'Artist Pro',
      audienceLabel: 'Industry professionals',
      price: '$75',
      note: '/mo',
      annualPrice: '$765',
      annualNote: '/yr',
      annualMonthly: '$63.75',
      audience: 'Single user',
      features: ['Dashboard', 'Catalog & Release Management', 'Career Assistant', 'Schedule & Calendar', 'Tasks', '24/7 Sync'],
      plusFeatures: ['Ticket Sales Tracker', 'Touring', 'Dizee AI Assistant', 'Finance Tracking'],
      popular: true,
    },
    {
      tier: 'Enterprise',
      audienceLabel: 'Industry professionals',
      price: '$125',
      note: '/mo',
      annualPrice: '$1,275',
      annualNote: '/yr',
      annualMonthly: '$106.25',
      audience: '2+ users ($15 per additional seat)',
      features: ['Dashboard', 'Catalog & Release Management', 'Career Assistant', 'Schedule & Calendar', 'Tasks', '24/7 Sync', 'Touring', 'Finance Tracking', 'Ticket Sales Tracker', 'Dizee AI Assistant'],
      plusFeatures: ['Teams', 'Songwriter Credits', 'Dedicated Customer Support'],
      popular: false,
    },
  ]
  return (
    <div>
      <SlideTag>Pricing</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Simple subscription.{' '}
        <span className="text-[#FF2300]">Predictable revenue.</span>
      </h2>
      <div className="mt-10 flex flex-col lg:flex-row gap-6 lg:gap-8 items-stretch">
        {tiers.map((p, i) => (
          <div key={i} className="relative flex-1 flex flex-col min-h-0">
            {i === 1 && (
              <p className="text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest mb-3">For industry professionals</p>
            )}
            {i === 0 && (
              <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">For independent artists</p>
            )}
            {i === 2 && <p className="text-[11px] font-semibold uppercase tracking-widest mb-3 invisible" aria-hidden>For industry professionals</p>}
            <div className={`flex-1 flex flex-col min-h-0 rounded-xl border-2 p-5 ${(i === 1 || i === 2) ? 'border-[#FF2300]/40 bg-[#FF2300]/[0.06] ring-1 ring-[#FF2300]/20' : 'border-white/[0.06] bg-white/[0.02]'}`}>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-semibold text-white">{p.tier}</h3>
                {p.popular && (
                  <span className="text-[9px] font-semibold text-[#FF2300] bg-[#FF2300]/10 rounded-full px-2 py-0.5">Popular</span>
                )}
              </div>
              {p.audienceLabel && <p className="text-base text-zinc-500 mb-2">{p.audienceLabel}</p>}
              <p className="text-2xl font-bold text-white mt-1">{p.price}<span className="text-[12px] font-normal text-zinc-500 ml-1">{p.note}</span></p>
              <p className="text-[13px] text-zinc-500 mt-0.5">Save 15% annually: <span className="text-zinc-400">{p.annualPrice}</span><span className="text-[11px] text-zinc-600 ml-0.5">{p.annualNote}</span> <span className="text-zinc-500">({p.annualMonthly}/mo)</span></p>
              <p className="text-base text-[#FF2300] font-medium mt-1">{p.audience}</p>
              <ul className="mt-4 space-y-1.5">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                    <span className="text-base text-zinc-400">{f}</span>
                  </li>
                ))}
              </ul>
              {p.plusFeatures && p.plusFeatures.length > 0 && (
                <>
                  <div className="my-3 border-t border-white/[0.06]" />
                  <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Plus</p>
                  <ul className="space-y-1.5">
                    {p.plusFeatures.map((f, j) => (
                      <li key={j} className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-[#FF2300] shrink-0" />
                        <span className="text-base text-zinc-300">{f}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideEndCap() {
  return (
    <div className="flex flex-col justify-center items-center text-center py-8">
      <p className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold tracking-tight text-white leading-snug max-w-4xl">
        <DizeeWord /> is building the modern operating system the music industry has been missing.
      </p>
      <p className="mt-8 text-lg sm:text-xl text-zinc-400 font-medium max-w-2xl">
        The product is in motion.{' '}
        <span className="text-[#FF2300]">Timing is now.</span>
      </p>
    </div>
  )
}

/** Same opening block as deck-final SlideClosing; product URL for this deck. */
function SlideClosing() {
  return (
    <div className="flex flex-col justify-center py-12">
      <div className="flex items-center gap-5 mb-8">
        <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-[#FF2300] rounded-sm flex-shrink-0" aria-hidden />
        <BrandLogo className="h-10 sm:h-12 lg:h-14 w-auto text-white" />
      </div>
      <p className="text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-white leading-snug max-w-3xl">
        The new operating system and source of truth for modern music careers.
      </p>
      <div className="mt-12 space-y-2">
        <p className="text-[15px] sm:text-[16px] text-[#FF2300] font-medium">www.dizee.com</p>
        <p className="text-base sm:text-[17px] text-white">
          <span className="text-[#FF2300] font-medium">Product</span>
          {' · Spring 2026'}
        </p>
      </div>
    </div>
  )
}

/*
 * Product story: Mission → layer & API → industry harness → Why Dizee → product pillars → Sheets → Summary → close.
 */
const SLIDES: Slide[] = [
  { title: 'Title', component: SlideTitle },
  { title: 'Mission', component: SlideMission },
  { title: 'The layer & API', component: SlideOperatingSystemAPI },
  { title: 'Industry harness', component: SlideIndustryHarness },
  { title: 'Why Dizee', component: SlideNarrativeCombined },
  { title: 'Dashboard', component: SlideDashboard },
  { title: 'Music', component: SlideMusic },
  { title: 'Shows', component: SlideShows },
  { title: 'Schedule & Calendar', component: SlideCalendar },
  { title: 'Teams', component: SlideAccess },
  { title: 'Tasks', component: SlideTasks },
  { title: 'AI intelligence', component: SlideAIIntelligence },
  { title: '24/7 sync', component: Slide247Sync },
  { title: 'Google Sheets', component: SlideSheets },
  { title: 'Summary', component: SlideSummary },
  { title: 'Pricing', component: SlidePricing },
  { title: 'End Cap', component: SlideEndCap },
  { title: 'Closing', component: SlideClosing },
]
